/* eslint-disable @typescript-eslint/no-explicit-any */
// ** 2. User Service - services/userService.ts
import apiService from './api'
import { sanitizeQueryParams } from '../utility/helpers/apiHelper'
import type { TableSortValue } from "@src/shared/constants/sort-order.constants";

interface User {
  id?: string | number
  name?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  phoneNumber?: string
  role?: string
  roleId?: number | string
  status?: string
  profileImage?: string
}

interface GetUsersParams {
  page?: number
  limit?: number
  search?: string
  role?: string
  status?: string
  orderBy?: string
  orderDirection?: TableSortValue
  [key: string]: any // For other dynamic params
}


export class UserService {
  // Get all users with optional filters
  async getAllUsers(query?: GetUsersParams) {
    if (query && Object.keys(query).length > 0) {
      return apiService.get('/users', { params: sanitizeQueryParams(query) })
    }
    return apiService.get('/users')
  }

  // Get users with pagination and filters (same as getAllUsers but kept for compatibility)
  async getUsers(query: GetUsersParams) {
    return apiService.get('/users', { params: sanitizeQueryParams(query) })
  }

  // Get single user by ID
  async getUser(id: string | number) {
    return apiService.get(`/users/${id}`)
  }

  // Create new user
  async createUser(userData: Partial<User>) {
    return apiService.post('/users', userData)
  }

  // Update user
  async updateUser(id: string | number, userData: Partial<User>) {
    return apiService.put(`/users/${id}`, userData)
  }

  // Delete user
  async deleteUser(id: string | number) {
    return apiService.delete(`/users/${id}`)
  }

  async resetPassword(data: { userId: number; password: string }) {
    return apiService.post('/users/reset-password', data)
  }

  // Patch user (partial update)
  async patchUser(id: string | number, userData: Partial<User>) {
    return apiService.patch(`/users/${id}`, userData)
  }

  // Get users with specific filters
  async getUsersByRole(role: string, query?: GetUsersParams) {
    const params = sanitizeQueryParams({ ...query, role })
    return apiService.get('/users', { params })
  }

  // Get users with search
  async searchUsers(searchTerm: string, query?: GetUsersParams) {
    const params = sanitizeQueryParams({ ...query, search: searchTerm })
    return apiService.get('/users', { params })
  }

  // Get paginated users
  async getPaginatedUsers(page: number = 1, limit: number = 10, query?: GetUsersParams) {
    const params = sanitizeQueryParams({ ...query, page, limit })
    return apiService.get('/users', { params })
  }

  // Bulk operations
  async bulkDeleteUsers(userIds: (string | number)[]) {
    return apiService.post('/users/bulk-delete', { userIds })
  }

  async bulkUpdateUsers(updates: { id: string | number; data: Partial<User> }[]) {
    return apiService.post('/users/bulk-update', { updates })
  }

  async getUserDropDown(){
    return apiService.get('/users/dropdown')
  }
}

// Export instance
export const userService = new UserService()
